import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';

export interface RadioOption<V extends string | boolean> {
  value: V;
  label: string;
}

interface FormRadioGroupProps<V extends string | boolean> {
  label: string;
  name: string;
  options: RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  error?: string;
  required?: boolean;
}

function FormRadioGroup<V extends string | boolean>({
  label,
  name,
  options,
  value,
  onChange,
  error,
  required,
}: FormRadioGroupProps<V>) {
  const { t } = useTranslation();
  const errorMessage = error ? t(`validation.${error}`, { defaultValue: error }) : undefined;

  return (
    <div className="flex flex-col gap-3 w-full" data-error={error ? 'true' : undefined}>
      <span className="text-base font-medium text-gray-700">
        {label}
        {required && <span className="text-gray-500 ms-1">*</span>}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isActive = value === opt.value;
          return (
            <button
              type="button"
              key={String(opt.value)}
              onClick={() => onChange(opt.value)}
              aria-pressed={isActive}
              className={cn(
                'inline-flex items-center justify-start text-start px-5 py-2.5 rounded-xl text-base font-medium border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-haat-red-500 focus-visible:ring-offset-2',
                isActive
                  ? 'bg-haat-red-600 text-white border-haat-red-600 shadow-md shadow-haat-red-100 hover:bg-haat-red-700 hover:border-haat-red-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-haat-red-300 hover:bg-haat-red-50/50 hover:text-haat-red-700',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {errorMessage && (
        <span className="text-xs font-medium text-red-600">{errorMessage}</span>
      )}
      <input type="hidden" name={name} value={value === null ? '' : String(value)} readOnly />
    </div>
  );
}

export default FormRadioGroup;
